import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import OffersCarouselComponent, { type OffersCarouselIndexProps } from '@cx-modules/offers-carousel';

import { OffersClickoutGuard } from '../offers-clickout';

// Shared OffersCarousel wrapper (NGF-6509), single source of truth for RR7 atlas and atlas-astro.
// Renders the @cx-modules/offers-carousel module with the app-agnostic pre-configuration RR7 applied
// inline: the clickout guard, the client-only analytics data-layer, and an ErrorBoundary. Mirrors the
// OffersList extraction (NGF-6397) — the one host-specific concern is decoupled:
//   - The error boundary takes an `onError` callback instead of importing @sentry/react-router (which
//     @cx-web/components cannot depend on). RR7 injects `captureException`; atlas-astro (no Sentry yet)
//     injects a console.error shim so failures still surface in the server log / browser console.
// Unlike OffersList this wrapper renders no wrapper divs and no Suspense: RR7 renders the carousel
// bare, and atlas-astro's withIsland already provides Suspense + an i18n provider.

export type OffersCarouselProps = Omit<OffersCarouselIndexProps, 'i18n'> & {
  /**
   * Called when the OffersCarousel subtree throws (including the OffersClickoutGuard's deliberate throw
   * on missing/malformed clickout props). RR7 atlas wires this to Sentry's captureException;
   * atlas-astro (no Sentry yet) wires a console.error shim so the failure still surfaces.
   */
  onError?: (error: unknown) => void;
};

const isFE = typeof window !== 'undefined';

class OffersCarouselErrorBoundary extends Component<
  { children: ReactNode; onError?: (error: unknown) => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError?.(error);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export const OffersCarousel = ({ onError, ...propsIn }: OffersCarouselProps) => {
  const { i18n } = useTranslation();

  // On the client, thread the GA data layer through for the module's impression/click analytics
  // (RR7 parity). On the server `window` is undefined, so only i18n is injected.
  const props = isFE
    ? {
        ...propsIn,
        analyticsDataLayer: window.dataLayer,
        i18n,
      }
    : {
        ...propsIn,
        i18n,
      };

  return (
    <OffersCarouselErrorBoundary onError={onError}>
      <OffersClickoutGuard componentName="OffersCarousel" values={props}>
        <OffersCarouselComponent {...props} />
      </OffersClickoutGuard>
    </OffersCarouselErrorBoundary>
  );
};

type OffersContentContractProps = {
  [key: string]: unknown;
  geoTargeting?: boolean | string;
  ocApiKey: string;
  repub: string;
  subdivision?: string;
};

export const OffersCarouselContentContractForPageTemplate = ({
  geoTargeting,
  ocApiKey,
  repub,
  subdivision,
  ...props
}: OffersContentContractProps) => {
  return {
    ...props,
    // NGF-6300: apiKey is the pre-resolved per-repub OC key threaded via ComponentLoaderContext.ocApiKey
    // (no ambient global ENV / per-repub cast here — Astro-island safe).
    apiKey: ocApiKey,
    subdivisionCode: subdivision,
    theme: `oc${repub.toLowerCase()}`,
    repub,
    geoTargeting,
    ...(geoTargeting === false
      ? {
          countryCode: undefined,
          subdivisionCode: undefined,
        }
      : {}),
  };
};
